import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import { useModifyTimeslotMutation } from "../../services/appln";
import { convertDate } from "../util";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@material-ui/icons/Edit";

interface FormData {
  name: string;
  desc: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

interface Props {
  bandobastId: string;
  timeslotId: string;
  initialValues: any;
}

export default function EditTimeslot(props: Props) {
  const [editTimeslot, editTimeslotParams] = useModifyTimeslotMutation();

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const iv: FormData = {
    name: props.initialValues.name,
    desc: props.initialValues.desc,
    startDate: props.initialValues.startTime.split(" ")[0],
    endDate: props.initialValues.endTime.split(" ")[0],
    startTime: props.initialValues.startTime
      .split(" ")[1]
      .split(":")
      .slice(0, 2)
      .join(":"),
    endTime: props.initialValues.endTime
      .split(" ")[1]
      .split(":")
      .slice(0, 2)
      .join(":"),
  };

  const onCancelHandlerWrapper = () => {
    editTimeslotParams.reset();
    handleClose();
  };

  console.log("editTimeslotParams", editTimeslotParams);
  React.useEffect(() => {
    if (editTimeslotParams.isSuccess) {
      handleClose();
    }
  }, [editTimeslotParams]);

  return (
    <div>
      <IconButton color="secondary" onClick={handleClickOpen}>
        <EditIcon />
      </IconButton>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {strings.editTimeslot}
        </DialogTitle>
        {editTimeslotParams.isError && (
          <ErrorMsg msg={strings.failedToEditTimeslot} />
        )}
        {editTimeslotParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              const timeRegEx = /[0-9][0-9]:[0-9][0-9]/g;
              if (!values.startDate) {
                errors.startDate = strings.startDateMandatory;
              }
              if (!values.startTime) {
                errors.startTime = strings.startTimeMandatory;
              } else if (!values.startTime.match(timeRegEx)) {
                errors.startTime = strings.invalidTimeFormat;
              }
              if (!values.endDate) {
                errors.endDate = strings.endDateMandatory;
              }
              if (!values.endTime) {
                errors.endTime = strings.endTimeMandatory;
              } else if (!values.endTime.match(timeRegEx)) {
                errors.endTime = strings.invalidTimeFormat;
              }
              if (
                values.startDate &&
                values.startTime &&
                values.endDate &&
                values.endTime
              ) {
                const startTimestamp = Date.parse(
                  `${values.startDate}T${values.startTime}:00.000Z`,
                );
                const endTimestamp = Date.parse(
                  `${values.endDate}T${values.endTime}:00.000Z`,
                );
                if (startTimestamp >= endTimestamp) {
                  errors.startDate = strings.startDateLaterThanEndDate;
                }
              }
              // TODO: check if start time and end time fall within start date and end date of bandobast
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const updatedValues = {
                name: values.name,
                desc: values.desc,
                startTime: `${convertDate(values.startDate)} ${
                  values.startTime
                }`,
                endTime: `${convertDate(values.endDate)} ${values.endTime}`,
              };
              editTimeslot({
                bandobastId: props.bandobastId,
                timeslotId: props.timeslotId,
                body: updatedValues,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="desc"
                        name="desc"
                        label={strings.description}
                        value={values.desc}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.desc && Boolean(errors.desc)}
                        helperText={touched.desc && errors.desc}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="startDate"
                        type="date"
                        name="startDate"
                        label={strings.startDate}
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="startTime"
                        name="startTime"
                        label={strings.startTime}
                        value={values.startTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startTime && Boolean(errors.startTime)}
                        helperText={touched.startTime && errors.startTime}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="endDate"
                        type="date"
                        name="endDate"
                        label={strings.endDate}
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endDate && Boolean(errors.endDate)}
                        helperText={touched.endDate && errors.endDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="endTime"
                        name="endTime"
                        label={strings.endTime}
                        value={values.endTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endTime && Boolean(errors.endTime)}
                        helperText={touched.endTime && errors.endTime}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.save}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
